import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, Megaphone, Eye, EyeOff } from "lucide-react";

export const PromoLinesTab = () => {
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState({ text: "", subtext: "", link: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/settings").then((r) => setLines(r.data.promo_lines || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.text.trim()) { toast.error("Enter a punch-line"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/promo-lines", form);
      setLines(r.data.promo_lines || []);
      setForm({ text: "", subtext: "", link: "" });
      toast.success("Punch-line added");
    } catch { toast.error("Could not add"); }
    setSaving(false);
  };

  const update = async (id, patch) => {
    const r = await api.put(`/admin/promo-lines/${id}`, patch);
    setLines(r.data.promo_lines || []);
  };
  const remove = async (id) => {
    const r = await api.delete(`/admin/promo-lines/${id}`);
    setLines(r.data.promo_lines || []);
    toast.success("Removed");
  };
  const move = async (idx, dir) => {
    const next = [...lines];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setLines(next);
    const r = await api.post("/admin/promo-lines/reorder", { order: next.map((l) => l.id) });
    setLines(r.data.promo_lines || []);
  };

  return (
    <div data-testid="promo-lines-tab">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="h-6 w-6 text-brand-red" />
        <div className="font-heading text-2xl text-brand-navy">Promo Punch-Lines</div>
      </div>
      <p className="text-slate-500 text-sm mb-5">Short punch-lines that rotate on the top of the home page. Only active ones show.</p>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 space-y-3">
        <div className="text-brand-red font-black uppercase tracking-widest text-xs">Add a punch-line</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Headline *" value={form.text} onChange={(v) => setForm({ ...form, text: v })} testId="promo-new-text" placeholder="Everything must go — up to 70% off!" />
          <Field label="Sub-text (optional)" value={form.subtext} onChange={(v) => setForm({ ...form, subtext: v })} testId="promo-new-subtext" placeholder="This weekend only" />
          <Field label="Link (optional)" value={form.link} onChange={(v) => setForm({ ...form, link: v })} testId="promo-new-link" placeholder="/deals or https://..." />
        </div>
        <button onClick={add} disabled={saving} data-testid="promo-add-btn"
          className="inline-flex items-center gap-1 bg-brand-red text-white rounded-full h-10 px-5 font-bold active:scale-95 disabled:opacity-60">
          <Plus className="h-4 w-4" /> Add punch-line
        </button>
      </div>

      <div className="space-y-2">
        {lines.length === 0 && <div className="text-slate-400 text-sm">No punch-lines yet.</div>}
        {lines.map((l, i) => (
          <div key={l.id} data-testid={`promo-row-${l.id}`}
            className={`bg-white border rounded-xl p-4 flex items-center gap-3 ${l.active ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`promo-up-${l.id}`} className="text-slate-400 hover:text-brand-navy disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === lines.length - 1} data-testid={`promo-down-${l.id}`} className="text-slate-400 hover:text-brand-navy disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-brand-navy">{l.text}</div>
              <div className="text-xs text-slate-500">{[l.subtext, l.link].filter(Boolean).join(" · ") || "—"}</div>
            </div>
            <button onClick={() => update(l.id, { active: !l.active })} data-testid={`promo-toggle-${l.id}`}
              className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 h-8 ${l.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {l.active ? <><Eye className="h-3.5 w-3.5" /> Active</> : <><EyeOff className="h-3.5 w-3.5" /> Hidden</>}
            </button>
            <button onClick={() => remove(l.id)} data-testid={`promo-delete-${l.id}`} className="text-brand-red p-1.5 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, testId, placeholder }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}</span>
    <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder}
      className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3" />
  </label>
);
